// src/pages/dashboard/receptionist-settings/settingsSchemas.ts
import { z } from 'zod';
import { daysOfWeek } from './settingsConstants';
import type { DayOfWeek } from './settingsTypes';

// --- Schemas for Main Settings Form ---
export const callActivationConfigSchema = z.object({
    enabled: z.boolean().default(false),
    timing_value: z.coerce
        .number({ invalid_type_error: 'Must be a number' })
        .int()
        .positive('Must be positive')
        .min(1, 'Must be at least 1')
        .default(1),
    timing_unit: z.enum(['minutes', 'hours', 'days']).default('hours'),
    max_age_days: z.coerce.number().int().positive().optional().default(7),
});

export const callActivationSettingsSchema = z.object({
    confirm_appointments: callActivationConfigSchema.extend({timing_value: z.coerce.number().int().positive().default(24), timing_unit: z.enum(['minutes', 'hours', 'days']).default('hours')}),
    recover_cancellations: callActivationConfigSchema.extend({timing_value: z.coerce.number().int().positive().default(1), timing_unit: z.enum(['minutes', 'hours', 'days']).default('hours')}),
    reschedule_noshows: callActivationConfigSchema.extend({timing_value: z.coerce.number().int().positive().default(15), timing_unit: z.enum(['minutes', 'hours', 'days']).default('minutes')}),
}).default({
    confirm_appointments: { enabled: false, timing_value: 24, timing_unit: 'hours', max_age_days: 7 },
    recover_cancellations: { enabled: false, timing_value: 1, timing_unit: 'hours', max_age_days: 7 },
    reschedule_noshows: { enabled: false, timing_value: 15, timing_unit: 'minutes', max_age_days: 7 },
});

export const aiCallingHoursDaySchema = z.object({
    enabled: z.boolean().default(true),
    start: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Invalid time (HH:mm)').default('09:00'),
    end: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Invalid time (HH:mm)').default('17:00'),
});

export const aiCallingHoursSchema = z.object({
    timezone: z.string().min(1, 'Timezone required').default('America/Denver'),
    days: z.object(
        daysOfWeek.reduce((acc, day) => {
            acc[day] = aiCallingHoursDaySchema.default({
                enabled: day !== 'Saturday' && day !== 'Sunday',
                start: '09:00',
                end: '17:00'
            });
            return acc;
        }, {} as Record<DayOfWeek, z.ZodDefault<typeof aiCallingHoursDaySchema>>)
    )
}).refine(
    (data) => {
        for (const day of daysOfWeek) {
            if (data.days[day].enabled && data.days[day].start >= data.days[day].end) {
                return false;
            }
        }
        return true;
    },
    {
        message: "End time must be after start time for enabled days.",
        path: ["days"],
    }
).default({
    timezone: 'America/Denver',
    days: {
        Monday: { enabled: true, start: '09:00', end: '17:00' },
        Tuesday: { enabled: true, start: '09:00', end: '17:00' },
        Wednesday: { enabled: true, start: '09:00', end: '17:00' },
        Thursday: { enabled: true, start: '09:00', end: '17:00' },
        Friday: { enabled: true, start: '09:00', end: '17:00' },
        Saturday: { enabled: false, start: '09:00', end: '17:00' },
        Sunday: { enabled: false, start: '09:00', end: '17:00' },
    }
});

// Main settings form schema (for user_settings table)
export const settingsSchema = z.object({
    ai_greeting_message: z.string().min(1, 'Greeting message is required').max(1000, 'Greeting message is too long'),
    ai_forwarding_number: z.string().regex(/^\+[1-9]\d{1,14}$/, { message: 'Invalid E.164 format (e.g., +12223334444)' }).or(z.literal('')).optional().nullable(),
    reply_to_email: z.string().min(1, 'Reply-To Email is required').email('Invalid email address'),
    ai_voice_id: z.string().min(1, 'Voice ID is required'),
    ai_voice_provider: z.string().min(1, 'Voice provider is required'),
    ai_name: z.string().max(100, 'AI name too long').optional().nullable(),
    business_name: z.string().max(255, 'Business name too long').optional().nullable(),
    call_activation_settings: callActivationSettingsSchema,
    ai_calling_hours: aiCallingHoursSchema,
    hipaa_enabled: z.boolean().default(false),
    ai_recording_preference: z.boolean().default(true),
    background_noise_enabled: z.boolean().default(true),
    business_address: z.string().max(1000, 'Address too long (max 1000 chars)').optional().nullable(),
    business_hours_raw: z.string().max(1000, 'Business hours description too long (max 1000 chars)').optional().nullable(),
    default_appointment_duration_minutes: z.coerce.number().int().positive("Duration must be positive").min(5, "Duration must be at least 5 minutes").optional().nullable(),
    call_retry_max_attempts: z.coerce.number().int().positive().min(1).default(3),
    call_retry_delay_minutes: z.coerce.number().int().positive().min(1).default(30),
    inbound_first_message_mode: z.enum(['assistant-speaks-first', 'assistant-speaks-first-with-model-generated-message', 'assistant-waits-for-user']).default('assistant-waits-for-user'),
    outbound_first_message_mode: z.enum(['assistant-speaks-first', 'assistant-speaks-first-with-model-generated-message', 'assistant-waits-for-user']).default('assistant-waits-for-user'),
    advanced_user_instructions_text: z.string().max(100000, 'Advanced instructions are too long (max 100,000 chars)').optional().nullable(), // NEW FIELD
    voicemail_instructions_text: z.string().max(10000, 'Voicemail instructions are too long (max 10,000 chars)').optional().nullable(),
});

// --- Schemas for Modals (Q&A, Site Links, Services) ---
export const qaItemFormSchema = z.object({
    question: z.string().min(1, 'Question is required').max(500, 'Question is too long'),
    answer: z.string().min(1, 'Answer is required').max(5000, 'Answer is too long'),
});

export const siteLinkFormSchema = z.object({
    question: z.string().min(1, 'URL is required').max(500, 'URL is too long')
        .refine(data => {
            try { new URL(data); return data.startsWith('http://') || data.startsWith('https://'); }
            catch (_) { return false; }
        }, { message: 'Invalid URL format (must start with http:// or https://)' }),
    answer: z.string().max(100000, 'Scraped content is too long (max 100,000 chars for edit)').optional(), // Increased limit
});

export const serviceItemFormSchema = z.object({
    service_name: z.string().trim().min(1, "Service name cannot be empty.").max(255, "Service name too long."),
    duration_minutes: z.coerce.number().int().positive("Duration must be a positive integer.").min(5, "Duration must be at least 5 minutes."),
    service_description: z.string().trim().max(10000, 'Description too long').optional(),
});